import { ApiClient } from '@momonos/api-client'
import type { SessionUser } from '@momonos/shared'
import { Button } from '@momonos/ui'
import { useMemo, useState } from 'react'

/** FR-AUTH-2 — email and password. */
export function SignIn({
  apiUrl,
  onSignedIn,
}: {
  apiUrl: string
  onSignedIn: (result: { accessToken: string; user: SessionUser }) => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setSubmitting] = useState(false)

  const api = useMemo(() => new ApiClient({ baseUrl: apiUrl }), [apiUrl])

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface p-6">
      <form
        className="w-full max-w-sm space-y-4"
        onSubmit={async (event) => {
          event.preventDefault()
          setSubmitting(true)
          setError(null)

          try {
            const result = await api.post<{ accessToken: string; user: SessionUser }>(
              '/v1/auth/login',
              { email, password },
            )
            onSignedIn(result)
          } catch (caught) {
            // The server deliberately does not say whether the email exists,
            // so neither does this.
            setError(
              (caught as { code?: string }).code === 'RATE_LIMITED'
                ? 'Too many attempts. Please wait a moment.'
                : 'Email or password is incorrect.',
            )
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <h1 className="font-bold text-3xl text-momo-white tracking-tight">MOMONOS</h1>
        <p className="text-content-muted">Sign in to the admin</p>

        <label className="block">
          <span className="mb-1 block text-content-secondary text-sm">Email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
            className="h-12 w-full rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-content-secondary text-sm">Password</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            className="h-12 w-full rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
          />
        </label>

        {error && (
          <p role="alert" className="rounded-lg bg-momo-red/15 px-3 py-2 text-momo-red text-sm">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" fullWidth isLoading={isSubmitting}>
          Sign in
        </Button>
      </form>
    </main>
  )
}
